/**
 * 🚀 GALLERY CACHE - Persistente Caching-Strategie
 * 
 * Stale-While-Revalidate (SWR) Pattern:
 * - Zeige sofort Daten aus Cache (localStorage)
 * - Revalidiere im Hintergrund
 * - Keine Spinner für bekannte Inhalte
 * - Überlebt App-Neustart!
 */

const STORAGE_KEY = 'snapshotpro_gallery_cache_v2'; // v2: After duplicate cleanup 2026-02-22
const CACHE_TTL = 30 * 60 * 1000; // 30 Minuten (länger für bessere UX)

// Memory-Cache (schneller Zugriff)
let memoryCache = {
  generations: null,
  videos: null,
  idCard: null,
  lastFetchGenerations: 0,
  lastFetchVideos: 0,
  lastFetchIdCard: 0,
  avatarId: null,
};

// Beim Start aus localStorage laden
const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Nur laden wenn nicht zu alt (max 1 Stunde)
      if (Date.now() - (parsed.savedAt || 0) < 60 * 60 * 1000) {
        memoryCache = { ...memoryCache, ...parsed };
        console.log('🚀 Cache aus localStorage geladen');
      }
    }
  } catch (e) {
    console.error('Cache load error:', e);
  }
};

// In localStorage speichern (debounced)
let saveTimeout = null;
export const saveToStorage = () => {
  clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...memoryCache,
        savedAt: Date.now()
      }));
    } catch (e) {
      console.error('Cache save error:', e);
    }
  }, 500);
};

// Initial laden
loadFromStorage();

/**
 * Generationen (Bilder) cachen
 */
export const getCachedGenerations = (avatarId) => {
  if (memoryCache.avatarId !== avatarId) {
    memoryCache.generations = null;
    memoryCache.videos = null;
    memoryCache.avatarId = avatarId;
  }
  return memoryCache.generations;
};

export const setCachedGenerations = (avatarId, data) => {
  memoryCache.avatarId = avatarId;
  memoryCache.generations = data;
  memoryCache.lastFetchGenerations = Date.now();
  saveToStorage();
};

export const isGenerationsCacheStale = () => {
  return Date.now() - memoryCache.lastFetchGenerations > CACHE_TTL;
};

/**
 * Videos cachen
 */
export const getCachedVideos = (avatarId) => {
  if (memoryCache.avatarId !== avatarId) {
    return null;
  }
  return memoryCache.videos;
};

export const setCachedVideos = (avatarId, data) => {
  memoryCache.avatarId = avatarId;
  memoryCache.videos = data;
  memoryCache.lastFetchVideos = Date.now();
  saveToStorage();
};

export const isVideosCacheStale = () => {
  return Date.now() - memoryCache.lastFetchVideos > CACHE_TTL;
};

/**
 * ID-Card Daten cachen (Pre-Fetching)
 */
export const getCachedIdCard = (avatarId) => {
  if (memoryCache.avatarId !== avatarId) return null;
  return memoryCache.idCard;
};

export const setCachedIdCard = (avatarId, data) => {
  memoryCache.avatarId = avatarId;
  memoryCache.idCard = data;
  memoryCache.lastFetchIdCard = Date.now();
  saveToStorage();
};

export const isIdCardCacheStale = () => {
  return Date.now() - memoryCache.lastFetchIdCard > CACHE_TTL;
};

/**
 * Einzelnes Item aus Cache entfernen (für Löschungen)
 */
export const removeFromCache = (type, itemId) => {
  if (type === 'video' && memoryCache.videos) {
    Object.keys(memoryCache.videos).forEach(key => {
      if (memoryCache.videos[key]?.id === itemId) {
        delete memoryCache.videos[key];
      }
    });
    saveToStorage();
  }
  if (type === 'generation' && memoryCache.generations) {
    memoryCache.generations = memoryCache.generations.filter(g => g.id !== itemId);
    saveToStorage();
  }
};

/**
 * Einzelnes Bild aus Generation im Cache entfernen (für Löschungen)
 */
export const removeImageFromCache = (genId, imageIndex) => {
  if (!memoryCache.generations) return;
  const gen = memoryCache.generations.find(g => g.id === genId);
  if (!gen) return;
  const images = gen.image_urls || [gen.image_url];
  if (images.length <= 1) {
    memoryCache.generations = memoryCache.generations.filter(g => g.id !== genId);
  } else {
    gen.image_urls = images.filter((_, idx) => idx !== imageIndex);
    if (gen.thumbnail_urls) {
      gen.thumbnail_urls = gen.thumbnail_urls.filter((_, idx) => idx !== imageIndex);
    }
  }
  saveToStorage();
};

/**
 * Cache komplett leeren (z.B. bei Logout)
 */
export const clearCache = () => {
  memoryCache = {
    generations: null,
    videos: null,
    idCard: null,
    lastFetchGenerations: 0,
    lastFetchVideos: 0,
    lastFetchIdCard: 0,
    avatarId: null,
  };
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Pre-fetch ID-Card Daten (aufrufen wenn Galerie sichtbar)
 */
export const prefetchIdCard = async (avatarId, token) => {
  if (!avatarId || !token) return;
  if (memoryCache.idCard && !isIdCardCacheStale()) return; // Schon gecacht
  
  try {
    const response = await fetch(`/api/training/${avatarId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setCachedIdCard(avatarId, data);
      console.log('🚀 ID-Card pre-fetched');
    }
  } catch (e) {
    console.error('Pre-fetch ID-Card error:', e);
  }
};

/**
 * Cache-Status für Debugging
 */
export const getCacheStatus = () => ({
  hasGenerations: !!memoryCache.generations,
  hasVideos: !!memoryCache.videos,
  hasIdCard: !!memoryCache.idCard,
  generationsCount: memoryCache.generations?.length || 0,
  videosCount: memoryCache.videos ? Object.keys(memoryCache.videos).length : 0,
  isGenerationsStale: isGenerationsCacheStale(),
  isVideosStale: isVideosCacheStale(),
  isIdCardStale: isIdCardCacheStale(),
});
